import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { collection, query, where, orderBy, onSnapshot, doc, updateDoc, getDoc } from 'firebase/firestore';
import { toast } from 'react-toastify';
import { FaFlag, FaCheck, FaTrash, FaChevronRight } from 'react-icons/fa';
import { format } from 'date-fns';
import Header from '../Header';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

function ReportedZone() {
  const { currentUser } = useAuth();
  const [reportedPosts, setReportedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!currentUser) return;

    // Check admin status
    const checkAdminStatus = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const isAdmin = userDoc.data().isAdmin || false;
          setIsAdmin(isAdmin);
          if (!isAdmin) {
            toast.error('You do not have permission to access this page');
            // Redirect to forum main page
            window.location.href = '/forum';
          }
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
      }
    };

    checkAdminStatus();

    // Set up reported posts listener
    const reportedQuery = query(
      collection(db, 'posts'),
      where('reportStatus', '==', 'active'),
      where('postStatus', '!=', 'removed'),
      orderBy('postStatus'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(reportedQuery, (snapshot) => {
      const posts = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setReportedPosts(posts);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching reported posts:', error);
      toast.error('Failed to load reported posts');
      setLoading(false);
    });

    return () => unsubscribe();
  }, [currentUser]);

  const handleResolve = async (postId) => {
    try {
      const postRef = doc(db, 'posts', postId);
      await updateDoc(postRef, {
        reportStatus: null,
        reportedBy: []
      });
      toast.success('Report resolved successfully');
    } catch (error) {
      console.error('Error resolving report:', error);
      toast.error('Failed to resolve report');
    }
  };

  const handleRemove = async (postId) => {
    try {
      const postRef = doc(db, 'posts', postId);
      await updateDoc(postRef, {
        postStatus: 'removed',
        reportStatus: null,
        reportedBy: []
      });
      toast.success('Post removed successfully');
    } catch (error) {
      console.error('Error removing post:', error);
      toast.error('Failed to remove post');
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Path */}
        <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-4">
          <Link to="/community" className="hover:text-purple-500 dark:hover:text-purple-400 transition-colors">
            Forum
          </Link>
          <FaChevronRight className="mx-2 text-gray-400" />
          <span className="text-gray-700 dark:text-gray-300">Reported Posts</span>
        </div>

        {/* Hero Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-2xl md:text-4xl min-h-12 font-bold bg-gradient-to-r from-indigo-500 to-purple-500 bg-clip-text text-transparent">
            Reported Posts
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Review and manage reported posts in the community forum.
          </p>
        </motion.div>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto"></div>
          </div>
        ) : reportedPosts.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-12 px-4"
          >
            <div className="flex flex-col items-center justify-center space-y-4">
              <div className="bg-gray-100 dark:bg-gray-700 rounded-full p-4">
                <FaFlag className="w-8 h-8 text-gray-400 dark:text-gray-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                No Reported Posts
              </h3>
              <p className="text-gray-600 dark:text-gray-400 max-w-sm">
                There are no posts that need attention at the moment.
              </p>
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {reportedPosts.map(post => (
              <motion.div
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg"
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                      {post.postTitle}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Posted by {post.username} on {format(post.createdAt.toDate(), 'MMM d, yyyy h:mm a')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-red-500 font-medium">
                      {post.reportedBy.length} {post.reportedBy.length === 1 ? 'report' : 'reports'}
                    </span>
                  </div>
                </div>

                <p className="text-gray-600 dark:text-gray-400 mb-6">{post.postContent}</p>

                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => handleResolve(post.id)}
                    className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center gap-2 transition-colors"
                  >
                    <FaCheck />
                    Resolve Report
                  </button>
                  <button
                    onClick={() => handleRemove(post.id)}
                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 flex items-center gap-2 transition-colors"
                  >
                    <FaTrash />
                    Remove Post
                  </button>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </main>
    </div>
  );
}

export default ReportedZone;
